import { useState, useEffect, useRef, useMemo } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { teacherPriorityApi } from '@/services/api/teacherPriority'
import { ChatParticipantResponse, Teacher } from '@/types/teacher-request'
import type { ChatMessagePage, ChatMessageResponse } from '@/types/teacher-chat'
import { TeacherSearchDropdown } from './TeacherSearchDropdown'

interface PrepChatPanelProps {
  requestId: string
  isReadOnly?: boolean
  requestingTeacherId?: string
  currentTeacherId?: string
  substituteTeacherId?: string
}

export function PrepChatPanel({ requestId, isReadOnly, requestingTeacherId, currentTeacherId, substituteTeacherId }: PrepChatPanelProps) {
  const queryClient = useQueryClient()
  const [message, setMessage] = useState('')
  const [showSearch, setShowSearch] = useState(false)
  const [sending, setSending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const messagesEndRef = useRef<HTMLDivElement>(null)

  const isRequestingTeacher = requestingTeacherId && currentTeacherId && requestingTeacherId === currentTeacherId

  const { data: participants = [] } = useQuery<ChatParticipantResponse[]>({
    queryKey: ['chat-participants', requestId],
    queryFn: () => teacherPriorityApi.getChatParticipants(requestId),
  })

  const { data: chatPage } = useQuery<ChatMessagePage>({
    queryKey: ['prep-chat-messages', requestId],
    queryFn: () => teacherPriorityApi.getChatMessages(requestId),
    refetchInterval: () => document.hidden ? false : 10000,
  })
  const messages: ChatMessageResponse[] = useMemo(() => chatPage?.messages ?? [], [chatPage])

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  const isParticipant = participants.some(
    p => p.teacher_id === currentTeacherId
  )

  const canManageParticipants = !isReadOnly && (isRequestingTeacher || isParticipant)

  const handleSend = async () => {
    if (!message.trim()) return
    setSending(true)
    setError(null)
    try {
      await teacherPriorityApi.sendChatMessage(requestId, message.trim())
      setMessage('')
      queryClient.invalidateQueries({ queryKey: ['prep-chat-messages', requestId] })
    } catch (e: any) {
      setError(e?.detail || 'Nachricht konnte nicht gesendet werden')
    } finally {
      setSending(false)
    }
  }

  const handleAddParticipant = async (teacher: Teacher) => {
    setError(null)
    try {
      await teacherPriorityApi.addChatParticipant(requestId, teacher.id)
      queryClient.invalidateQueries({ queryKey: ['chat-participants', requestId] })
    } catch (e: any) {
      setError(e?.detail || 'Teilnehmer konnte nicht hinzugefügt werden')
    }
  }

  const handleRemoveParticipant = async (teacherId: string) => {
    if (!confirm('Teilnehmer wirklich entfernen?')) return
    setError(null)
    try {
      await teacherPriorityApi.removeChatParticipant(requestId, teacherId)
      queryClient.invalidateQueries({ queryKey: ['chat-participants', requestId] })
    } catch (e: any) {
      setError(e?.detail || 'Teilnehmer konnte nicht entfernt werden')
    }
  }

  const getInitials = (name: string) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
  }

  const groupMessagesByDate = (messages: ChatMessageResponse[]) => {
    const groups: Record<string, ChatMessageResponse[]> = {}
    messages.forEach(msg => {
      const date = new Date(msg.created_at).toLocaleDateString('de-CH')
      if (!groups[date]) groups[date] = []
      groups[date].push(msg)
    })
    return groups
  }

  const groupedMessages = groupMessagesByDate(messages)
  const participantIds = participants.map(p => p.teacher_id)

  return (
    <div className="bg-white rounded-xl shadow-sm border flex flex-col h-full">
      <div className="relative z-20 p-3 border-b bg-gray-50 rounded-t-xl">
        <div className="flex justify-between items-center">
          <h3 className="text-sm font-semibold text-gray-700">Chat</h3>
          {canManageParticipants && (
          <div className="relative">
            <button
              onClick={() => setShowSearch(!showSearch)}
              className="text-xs bg-gray-100 border rounded-lg px-2 py-1 hover:bg-gray-200 flex items-center gap-1"
            >
              <span>👥</span> {participants.length} Teilnehmer <span className="text-blue-500">+</span>
            </button>
            {showSearch && (
              <TeacherSearchDropdown
                excludeTeacherIds={participantIds}
                onSelect={handleAddParticipant}
                onClose={() => setShowSearch(false)}
                align="right"
              />
            )}
          </div>
          )}
        </div>
        <div className="flex gap-2 mt-2 flex-wrap">
          {participants.map(p => (
            <span key={p.id} className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 rounded-full text-[11px] text-gray-600">
              <span className="w-1.5 h-1.5 rounded-full bg-gray-300" />
              {p.teacher_name}
              {canManageParticipants && p.teacher_id !== substituteTeacherId && p.id !== "requesting" && (
              <button
                aria-label="Teilnehmer entfernen"
                onClick={() => handleRemoveParticipant(p.teacher_id)}
                className="text-gray-400 hover:text-red-500 ml-0.5"
              >
                ×
              </button>
              )}
            </span>
          ))}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-3 space-y-3">
        {Object.entries(groupedMessages).map(([date, msgs]) => (
          <div key={date}>
            <div className="text-center"><span className="text-[10px] text-gray-400 bg-gray-50 px-2 py-0.5 rounded">{date}</span></div>
            {msgs.map(msg => (
              <div key={msg.id} className="flex gap-2 mt-2">
                <div className="w-7 h-7 rounded-full bg-blue-500 flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0">
                  {getInitials(msg.sender_name)}
                </div>
                <div>
                  <div className="text-[10px] text-gray-400 mb-0.5">{msg.sender_name} · {new Date(msg.created_at).toLocaleTimeString('de-CH', { hour: '2-digit', minute: '2-digit' })}</div>
                  <div className="bg-gray-100 rounded-lg rounded-tl-none p-2.5 text-sm text-gray-700 max-w-xs">
                    {msg.body}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      {error && (
        <div className="px-3 py-2 bg-red-50 border-t border-red-200 text-xs text-red-700">
          {error}
        </div>
      )}

      {isReadOnly ? (
      <div className="p-3 border-t bg-gray-50 rounded-b-xl">
        <div className="flex items-center justify-center text-xs text-gray-400">
          Dieser Chat ist archiviert (read-only)
        </div>
      </div>
      ) : (
      <div className="p-3 border-t bg-gray-50 rounded-b-xl">
        <div className="flex gap-2">
          <input
            type="text"
            value={message}
            onChange={e => setMessage(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSend()}
            placeholder="Nachricht schreiben..."
            className="flex-1 px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
            disabled={sending}
          />
          <button
            onClick={handleSend}
            disabled={sending || !message.trim()}
            className="px-3 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 text-sm"
          >
            {sending ? '...' : 'Senden'}
          </button>
        </div>
      </div>
      )}
    </div>
  )
}
